import { create } from 'zustand';
import { coreLoopConfigurationActions } from './coreLoopConfiguration.actions';
import { ICoreLoopConfigurationItems } from './coreLoopConfiguration.types';
import { CoreLoopConfiguration } from '../generated/gql-types';

export type CoreLoopConfigurationStatus = 'idle' | 'loading' | 'ready' | 'error';

const inFlightRequests = new Map<string, Promise<CoreLoopConfiguration | null>>();

interface CoreLoopConfigurationState {
  coreLoopConfiguration: CoreLoopConfiguration | null;
  coreLoopConfigurationItems: ICoreLoopConfigurationItems | null;
  coreLoopConfigurationStatus: CoreLoopConfigurationStatus;
  loadedTenantId: string | null;
  setCoreLoopConfiguration: (
    coreLoopConfiguration: CoreLoopConfiguration,
  ) => void;
  setCoreLoopConfigurationItems: (
    coreLoopConfigurationItems: ICoreLoopConfigurationItems,
  ) => void;
  setCoreLoopConfigurationStatus: (
    coreLoopConfigurationStatus: CoreLoopConfigurationStatus,
  ) => void;
  clearCoreLoopConfigurationCache: () => void;
  fetchAndSetCoreLoopConfiguration: (
    tenantId: string,
  ) => Promise<CoreLoopConfiguration | null>;
  ensureCoreLoopConfiguration: (
    tenantId: string,
  ) => Promise<CoreLoopConfiguration | null>;
}

export const useCoreLoopConfigurationStore = create<CoreLoopConfigurationState>(
  (set, get) => ({
    //   state
    coreLoopConfiguration: null,
    coreLoopConfigurationItems: null,
    coreLoopConfigurationStatus: 'idle',
    loadedTenantId: null,
    //   actions
    setCoreLoopConfiguration: (coreLoopConfiguration) =>
      set({ coreLoopConfiguration }),
    setCoreLoopConfigurationItems: (coreLoopConfigurationItems) =>
      set({ coreLoopConfigurationItems }),
    setCoreLoopConfigurationStatus: (coreLoopConfigurationStatus) =>
      set({ coreLoopConfigurationStatus }),
    clearCoreLoopConfigurationCache: () =>
      set({
        coreLoopConfiguration: null,
        coreLoopConfigurationItems: null,
        coreLoopConfigurationStatus: 'idle',
        loadedTenantId: null,
      }),
    fetchAndSetCoreLoopConfiguration: async (tenantId: string) => {
      set({ coreLoopConfigurationStatus: 'loading' });
      const coreLoopConfiguration =
        await coreLoopConfigurationActions.fetchAndSetCoreLoopConfiguration(
          tenantId,
        );
      if (coreLoopConfiguration) {
        set({ coreLoopConfiguration });
        if (coreLoopConfiguration.configuration) {
          set({
            coreLoopConfigurationItems:
              coreLoopConfiguration.configuration as ICoreLoopConfigurationItems,
            coreLoopConfigurationStatus: 'ready',
            loadedTenantId: tenantId,
          });
        } else {
          set({
            coreLoopConfigurationItems: null,
            coreLoopConfigurationStatus: 'ready',
            loadedTenantId: tenantId,
          });
        }
        return coreLoopConfiguration;
      } else {
        set({ coreLoopConfigurationStatus: 'error' });
        return null;
      }
    },
    ensureCoreLoopConfiguration: async (tenantId: string) => {
      const {
        loadedTenantId,
        coreLoopConfigurationStatus,
        fetchAndSetCoreLoopConfiguration,
      } = get();

      if (
        loadedTenantId === tenantId &&
        coreLoopConfigurationStatus === 'ready'
      ) {
        return get().coreLoopConfiguration;
      }

      if (inFlightRequests.has(tenantId)) {
        return inFlightRequests.get(tenantId)!;
      }

      const request = fetchAndSetCoreLoopConfiguration(tenantId).finally(() => {
        inFlightRequests.delete(tenantId);
      });

      inFlightRequests.set(tenantId, request);
      return request;
    },
  }),
);
